import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { CSVLink } from 'react-csv';
import moment from 'moment-timezone';
import ApiService from '../../ApiService';
import { Dealer } from '../../types/Dealer';
import { TrackingStatus } from '../../types/HAT/Tracking';

// Order matches the advertisers table columns in AgencyEditPage.
const csvHeaders = [
    'Advertiser',
    'Status',
    'Website',
    'Polk Dealer Assigned',
    'HAT Tracking Status',
    'Amazon DSP Advertiser ID',
    'Amazon DSP Advertiser Status',
    'AMC Instance Created',
];

function getCsvFilename(agencyName?: string): string {
    const namePart = (agencyName || 'Agency').replace(/[^a-zA-Z0-9]+/g, '_');
    return `Agency Advertisers_${namePart}_${moment().format('YYYYMMDD')}.csv`;
}

function dspAdvertiserStatusValue(dealer: Dealer, advertiserNames?: Record<string, string>): string {
    const id = dealer.dspAdvertiser;
    if (!id) {
        return '';
    }
    const name = advertiserNames?.[id];
    if (!name) {
        return 'Invalid Advertiser ID';
    }
    return name === dealer.dealerName ? name : `${name} (name mismatch)`;
}

// Mirrors the labels shown by TrackingStatusChip. `status` is the TrackingStatus enum name from the
// bulk map (keyed by dealer id); blank when the dealer isn't in the map.
function hatTrackingStatusValue(status?: string): string {
    switch (status) {
        case TrackingStatus.NEW:
            return 'Pending Tracking';
        case TrackingStatus.ALIVE:
            return 'Tracking';
        case TrackingStatus.DEAD:
            return 'Stopped Tracking';
        case TrackingStatus.INVALID:
            return 'Invalid Tracking Code';
        default:
            return '';
    }
}

interface AgencyAdvertiserExportCsvButtonProps {
    dealers: Dealer[];
    dspAdvertiserNames?: Record<string, string>;
    agencyId?: number;
    agencyName?: string;
}

const AgencyAdvertiserExportCsvButton = ({
    dealers,
    dspAdvertiserNames,
    agencyId,
    agencyName,
}: AgencyAdvertiserExportCsvButtonProps) => {
    // dealerId -> TrackingStatus name, fetched on demand from the cached bulk endpoint.
    const [hatStatuses, setHatStatuses] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [pendingDownload, setPendingDownload] = useState(false);
    const csvLink = useRef<any | null>(null);

    const csvData = useMemo(
        () =>
            dealers.map((dealer) => [
                dealer.dealerName ?? '',
                dealer.activeStatus ? 'Active' : 'Inactive',
                (dealer.url || '').trim(),
                dealer.polkDealerName || '',
                hatTrackingStatusValue(dealer.id != null ? hatStatuses[String(dealer.id)] : undefined),
                dealer.dspAdvertiser || '',
                dspAdvertiserStatusValue(dealer, dspAdvertiserNames),
                dealer.amcInstanceId ? 'Yes' : 'No',
            ]),
        [dealers, dspAdvertiserNames, hatStatuses]
    );

    // Once the HAT map has been fetched, trigger the (hidden) CSVLink download.
    useEffect(() => {
        if (pendingDownload && csvLink.current?.link) {
            csvLink.current.link.click();
            setPendingDownload(false);
        }
    }, [pendingDownload, csvData]);

    const handleExport = async () => {
        if (!agencyId) {
            return;
        }
        setLoading(true);
        try {
            const response = await ApiService.getAgencyHatTrackingStatuses(agencyId);
            setHatStatuses(response.data ?? {});
        } catch {
            // Fall back to exporting without HAT statuses rather than failing the whole export.
            setHatStatuses({});
        } finally {
            setLoading(false);
            setPendingDownload(true);
        }
    };

    return (
        <>
            <Button variant="outlined" size="small" onClick={handleExport} disabled={dealers.length === 0 || loading}>
                {loading ? 'Exporting…' : 'Export CSV'}
            </Button>
            <CSVLink
                ref={csvLink}
                headers={csvHeaders}
                data={csvData}
                filename={getCsvFilename(agencyName)}
                asyncOnClick={true}
                style={{ display: 'none' }}
            />
        </>
    );
};

export default AgencyAdvertiserExportCsvButton;
